import React, { Suspense, useEffect, useState, useContext } from "react";
import { lazy } from "react";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
// First-paint / shell screens stay eager so the initial render isn't gated on a
// second chunk fetch.
import Prepare from "../screens/prepare";
import Leading from "../screens/leading";
import LandingPage from "../screens/landing";
import ForgotPassword from "../screens/forgotPassword";
import MainLayout from "./layout/MainLayout";
import PrepareBundle from "../screens/prepareBundle";
import Exam from "../screens/onboard/exam";
import { IS_BUNDLE_APP } from "../../app_constant";
import { IS_WHITE_LABEL, CROSS_PROMO_ENABLED } from "../utils/brandFlags";
import { isExamEntitledFor } from "../utils/subscriptionScope";
import Loader from "./loader";
import { AuthContext } from "../context/AuthProvider";
import { BundleContext } from "../context/BundleProvider";

// Heavy authenticated-area screens are code-split — each becomes its own chunk fetched
// only when its route is visited, keeping them out of the initial bundle. All routes
// below render inside <Suspense fallback={<Loader />}>.
const TestReview = lazy(() => import("../screens/test/testReview"));
const QuestionReview = lazy(() => import("../screens/questionReview"));
const Profile = lazy(() => import("../screens/profile"));
const Account = lazy(() => import("../screens/profile/account"));
const EditLoginMethods = lazy(() => import("../screens/profile/EditLoginMethods"));
const TestSetting = lazy(() => import("../screens/profile/components/testSetting"));
const OtherApps = lazy(() => import("../screens/otherApps"));
const HelpAndSupport = lazy(() => import("../screens/helpAndSupport"));
const Glossary = lazy(() => import("../screens/glossary"));
const StudyQuestion = lazy(() => import("../screens/studyQuestion"));
const QuickSet = lazy(() => import("../screens/quickSet"));
const PracticeTest = lazy(() => import("../screens/practiceTest"));
const FlashCards = lazy(() => import("../screens/flashCards"));
const QuickSets = lazy(() => import("../screens/flashCards/QuickSets"));
const CustomTestBuilder = lazy(() => import("../screens/customTestBuilder"));
const Articles = lazy(() => import("../screens/articles"));
const Article = lazy(() => import("../screens/articles/article"));
const Dashboard = lazy(() => import("../screens/dashboard/dashboard"));
const History = lazy(() => import("../screens/history"));
const Bookmark = lazy(() => import("../screens/bookmark"));
const Test = lazy(() => import("../screens/test"));
const MainBookmark = lazy(() => import("../screens/MainBookmark"));
const ChangeApperance = lazy(() => import("../screens/changeApperance"));
const Questions = lazy(() => import("../screens/studyQuestion/questions"));
const UserExamSchedule = lazy(() => import("../screens/UserExamSchedule"));
const OnboardingTest = lazy(() => import("../screens/onboard/OnboardingTest"));
const OnboardingTestReview = lazy(() => import("../screens/onboard/OnboardingTestReview"));
const OnboardingQuestionReview = lazy(() => import("../screens/onboard/OnboardingQuestionReview"));

const Acronyms = lazy(() => import("../screens/acronyms"));
const Home = lazy(() => import("../screens/home"));
const ChoosePlan = lazy(() => import("../screens/choosePlans"));

// White-label "no access" gate (activation-code reactivation). Activation SIGNUP is a modal
// (the WL SignupModal opened from Home), not a route.
const NotSubscribed = lazy(() => import("../screens/NotSubscribed"));

const QOTDAnswerScreen = lazy(() => import("../screens/qotd/QOTDAnswerScreen"));
const QOTDExplanationScreen = lazy(() => import("../screens/qotd/QOTDExplanationScreen"));
const QOTDHistory = lazy(() => import("../screens/qotd/QOTDHistory"));

const PageRouter = () => {
  const [isReloading, setIsReloading] = useState(false);
  const location = useLocation();
  const { isAuthenticated, isAuthLoading, profile, bundleData } = useContext(AuthContext);
  const { selectedCertificate } = useContext(BundleContext);

  // Redirect authenticated bundle-app users to /prepare when no certificate is set
  // Prepare handles initialization and shows select-exam modal for first-time users
  const needsPrepareRedirect =
    isAuthenticated &&
    IS_BUNDLE_APP &&
    !selectedCertificate &&
    location.pathname !== "/prepare";

  const handleReload = () => {
    setIsReloading(true);
    // Reset reloading state after a short delay to prevent stuck loading state
    setTimeout(() => {
      setIsReloading(false);
    }, 100);
  };

  useEffect(() => {
    window.addEventListener("beforeunload", handleReload);
    // Cleanup the subscription when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleReload);
    };
  }, []);

  // Show loader only when auth is loading or during reload
  if (isAuthLoading || isReloading) {
    return <Loader />;
  }

  // Redirect to prepare when authenticated but no certificate (first-time or refresh)
  if (needsPrepareRedirect) {
    return <Navigate to="/prepare" replace />;
  }

  // White-label: an authenticated user whose SELECTED exam has no live entitlement must reach the
  // reactivation screen (otherwise they silently drop to free-tier caps with no redeem path). Derive
  // entitlement SYNCHRONOUSLY here (pure) rather than reading AuthProvider's effect-derived
  // `isSubscribe`, which lags the render where `profile` first lands — that lag would bounce a
  // genuinely-entitled user through /not-subscribed on first load. Only gate once the profile/bundle
  // doc has loaded, and let them leave /not-subscribed the moment entitlement is present.
  if (IS_WHITE_LABEL && isAuthenticated && selectedCertificate) {
    const hasEntitlement =
      isExamEntitledFor(profile, selectedCertificate) ||
      isExamEntitledFor(bundleData, selectedCertificate);
    if (hasEntitlement && location.pathname === "/not-subscribed") {
      return <Navigate to="/home" replace />;
    }
    const profileLoaded = !!(profile || bundleData);
    if (
      profileLoaded &&
      !hasEntitlement &&
      location.pathname !== "/not-subscribed" &&
      location.pathname !== "/prepare"
    ) {
      return <Navigate to="/not-subscribed" replace />;
    }
  }

  return (
    <>
      {!isAuthenticated ? (
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/leading" element={<Leading />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/forgot_password" element={<ForgotPassword />} />
            <Route path="/welcome" element={<Exam />} />
            <Route path="/onboarding-test" element={<OnboardingTest />} />
            <Route path="/onboarding-test-review" element={<OnboardingTestReview />} />
            <Route path="/onboarding-question-review" element={<OnboardingQuestionReview />} />
            <Route path="/questionReview" element={<QuestionReview />} />
            <Route path="/exam" element={<Exam />} />
            <Route
              path="/prepare"
              element={
                IS_BUNDLE_APP ? (
                  <PrepareBundle handleReload={handleReload} />
                ) : (
                  <Prepare />
                )
              }
            />
            <Route element={<MainLayout />}>
              <Route path="/home" element={<Home />} />
              <Route path="*" element={<Home />} />
            </Route>
          </Routes>
        </Suspense>
      ) : (
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route
              path="/prepare"
              element={
                IS_BUNDLE_APP ? (
                  <PrepareBundle handleReload={handleReload} />
                ) : (
                  <Prepare />
                )
              }
            />
            <Route element={<MainLayout />}>
              {/* White-label brands have no paywall: bounce any stray /choosePlan
                  navigation back home. Non-WL brands keep the Stripe plan picker. */}
              <Route
                path="/choosePlan"
                element={IS_WHITE_LABEL ? <Navigate to="/home" replace /> : <ChoosePlan />}
              />
              {/* White-label expired/no-entitlement gate (activation-code reactivation). */}
              {IS_WHITE_LABEL && (
                <Route path="/not-subscribed" element={<NotSubscribed />} />
              )}
              <Route path="/testReview" element={<TestReview />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/account" element={<Account />} />
              <Route path="/login-methods" element={<EditLoginMethods />} />
              <Route path="/appearance" element={<ChangeApperance />} />
              <Route path="/testSetting" element={<TestSetting />} />
              {CROSS_PROMO_ENABLED && (
                <Route path="/otherApps" element={<OtherApps />} />
              )}
              <Route path="/helpAndSupport" element={<HelpAndSupport />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/acronyms" element={<Acronyms />} />
              <Route path="/studyQuestion" element={<StudyQuestion />} />
              <Route path="/quick" element={<QuickSet />} />
              <Route path="/practicetest" element={<PracticeTest />} />
              <Route path="/flashcard" element={<FlashCards />} />
              <Route path="/flashcard/quick" element={<QuickSets />} />
              <Route path="/flashcard/questions" element={<QuickSets />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/article" element={<Article />} />
              <Route path="/customtest" element={<CustomTestBuilder />} />
              <Route path="/home" element={<Home />} />
              <Route path="*" element={<Home />} />
              <Route path="/studyquestions/:id" element={<Questions />} />
              <Route path="/" element={<Home />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/history" element={<History />} />
              <Route path="/Bookmark" element={<MainBookmark />} />
              <Route path="/Bookmark/questions" element={<Bookmark />} />
              <Route path="/test" element={<Test />} />
              <Route path="/questionReview" element={<QuestionReview />} />
              <Route path="/user-exam-schedule" element={<UserExamSchedule />} />
              <Route path="/qotd/answer" element={<QOTDAnswerScreen />} />
              <Route path="/qotd/explanation" element={<QOTDExplanationScreen />} />
              <Route path="/qotd/history" element={<QOTDHistory />} />
            </Route>
          </Routes>
        </Suspense>
      )}
    </>
  );
};

export default PageRouter;
